import { Search, Package, MapPin, Calculator, Truck } from "lucide-react";
import React, { useState } from "react";
import { Button } from "/components/Buttons";
import TrackResult from "/components/TrackResult";
import MountainRidge from "components/MountainRidge";
import { getNewsListUser } from "context/actions/news";
import useSiteSettings from "utils/useSiteSettings";

const scrollTo = (id) => {
  const el = typeof document !== "undefined" && document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = ({ linkData }) => {
  const { get } = useSiteSettings();
  const [value, setValue] = useState("");
  const [data, setData] = useState([]);
  const [check, setCheck] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onChangeName = (event) => {
    setValue(event.target.value);
    if (error) setError("");
  };

  const handleButtonClick = async () => {
    if (!value.trim()) {
      setError("Утас эсвэл трак кодоо оруулна уу");
      return;
    }
    setLoading(true);
    try {
      const res = await getNewsListUser(value, 1);
      if (res.status === "success") {
        setData(res.data.data);
        setCheck(1);
      } else {
        // Сервер зөвхөн 8 оронтой утас эсвэл бүтэн трак кодоор хайдаг
        setData([]);
        setCheck(0);
        setError(
          res.message === "track.invalid_query"
            ? "8 оронтой утасны дугаар эсвэл бүтэн трак кодоо оруулна уу"
            : "Хайхад алдаа гарлаа. Дахин оролдоно уу."
        );
      }
    } catch (e) {
      setError("Хайхад алдаа гарлаа. Дахин оролдоно уу.");
    } finally {
      setLoading(false);
    }
  };

  const badges = [
    { icon: Package, label: get("hero.badge1"), target: "track" },
    { icon: MapPin, label: get("hero.badge2"), target: "branches" },
    { icon: Calculator, label: get("hero.badge3"), target: "services" },
  ];

  return (
    <section id="top" className="relative overflow-hidden bg-brand-blue-dark text-white">
      {/* Дэвсгэр: өндөршлийн шугам + оргилын ард гэрэлтэлт */}
      <div className="absolute inset-0 bg-topo pointer-events-none" />
      <div className="absolute -top-64 left-1/2 -translate-x-1/2 w-[960px] h-[640px] rounded-full bg-brand-blue/50 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 pt-14 pb-32 md:pt-20 md:pb-48 relative z-10">
        <div className="max-w-3xl mx-auto text-center animate-in fade-in slide-in-from-bottom-7 duration-700">
          {/* Чиглэл: Эрээн → Говь-Алтай */}
          <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white/80">
            <span>Эрээн</span>
            <span className="flex items-center gap-1 w-16 sm:w-24" aria-hidden="true">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="flex-1 border-t border-dashed border-white/40" />
              <Truck className="w-4 h-4 text-primary" />
              <span className="flex-1 border-t border-dashed border-white/40" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            </span>
            <span>Говь-Алтай</span>
          </div>

          <h1 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight">
            {get("hero.title")}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
            {get("hero.subtitle")}
          </p>

          {/* Илгээмж хянах — hero-гийн гол үйлдэл */}
          <div id="track" className="mt-9 scroll-mt-28 text-left">
            <div className="rounded-2xl bg-white p-2 shadow-2xl shadow-black/30">
              <label htmlFor="trackInput" className="sr-only">
                Илгээмжээ хянах
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    id="trackInput"
                    value={value}
                    onChange={onChangeName}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleButtonClick();
                    }}
                    placeholder={get("hero.searchPlaceholder")}
                    className={`w-full h-14 pl-12 pr-3 rounded-xl border bg-slate-50 text-foreground text-base outline-none transition-colors ${
                      error
                        ? "border-destructive ring-2 ring-destructive/20"
                        : "border-transparent focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/25"
                    }`}
                  />
                </div>
                <Button
                  onClick={handleButtonClick}
                  disabled={loading}
                  className="h-14 px-7 rounded-xl bg-primary hover:bg-primary-hover text-white text-base font-bold"
                >
                  {loading ? (
                    <span className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                  {loading ? "Хайж байна…" : "Илгээмж шалгах"}
                </Button>
              </div>

              {(error || check === 1) && (
                <div className="px-3 pb-3">
                  {error && <p className="mt-3 text-sm font-medium text-destructive">{error}</p>}
                  {check === 1 && <TrackResult data={data} checked />}
                </div>
              )}
            </div>
            <p className="mt-3 text-center text-xs text-white/55">
              8 оронтой утасны дугаар эсвэл бүтэн трак кодоор хайна
            </p>
          </div>

          <div className="mt-7 flex flex-wrap gap-2.5 justify-center">
            {badges.map((b, i) => (
              <button
                key={i}
                onClick={() => scrollTo(b.target)}
                className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-white/15 bg-white/5 text-sm font-semibold text-white/85 hover:border-primary/70 hover:bg-white/10 transition-colors"
              >
                <b.icon className="h-4 w-4 text-primary" /> {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Доод ирмэг: Оргил — уулын нуруу (урд давхарга нь дараагийн хэсгийн өнгөтэй) */}
      <MountainRidge
        layers={["fill-brand-blue/70", "fill-white/15", "fill-white"]}
        className="absolute bottom-0 inset-x-0 h-24 md:h-36 lg:h-44"
      />
    </section>
  );
};

export default Hero;
